import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fromSavedFiltersDoc, toSavedFiltersDoc } from '@/lib/savedSearchDoc';
import { PRIMARY_ACTIVE_CITY } from '@/lib/geoScope';
import type { SourcePostFilters } from '@/hooks/useSourcePosts';
import { poprawnaLista } from '@/lib/bliskoDo';
import { maCene } from '@/lib/mojeFiltry';
import { uporzadkujObszary } from '@/lib/areaSelection';
import { SAVED_FILTERS_VERSION, type RecentPlace, type SaveMyFiltersInput, type SavedSearchCadence, type SavedSearchRow } from '@/types/savedSearch';

/**
 * MOJE FILTRY — THE TENANT'S SAVED SET AS THE RESULTS PAGE SEES IT (plan E1.2, build plan F2).
 *
 * One row per tenant in `flat.saved_searches` (F1). Read and written ONLY through the two
 * SECURITY DEFINER RPCs `flat.get_my_filters()` / `flat.save_my_filters(p_doc)`: they take the
 * identity from `auth.uid()`, so there is no id to pass and nothing for a caller to forge
 * (Convention 32). The full `SourcePostFilters` round-trips through the document codec
 * (`src/lib/savedSearchDoc.ts`) — stations, estates, drawn areas, POI, "od zaraz" and
 * "opublikowane" are stored like everything else; nothing is dropped any more.
 *
 * WHO HAS A SET. Owners and agents get zero rows from the RPC; a guest never calls
 * it. Both come back as `PUSTE` (`isTenant: false`): no prefill, no save. A tenant with no row
 * yet gets an empty unsaved row on first read or write (the RPC creates it), so a tenant always
 * has `isTenant: true`. Onboarding writes its answers straight into this row as the unsaved
 * prefill (plan §1.89, `src/lib/onboardingCriteria.ts`): the city step's districts, „Czego
 * szukasz?”'s budget, rooms, area and furnishing; the C5 e-mail box additionally saves it with
 * alerts on (`src/lib/onboardingEmailAlerts.ts`). There is no other copy of the criteria.
 *
 * NOT MOJE FILTRY YET. `savedAt === null` means the row only prefills (onboarding/backfill,
 * §1.71): the results page opens with it exactly as before, but the flag is unlit and the
 * person has not saved anything. It becomes Moje filtry on the first save that carries a price
 * (§1.3) — see `saveMyFilters`.
 */
export interface MojeWyszukiwanie {
  /** The set in the results page's shape (`fromSavedFiltersDoc`). Empty when nothing is set. */
  filters: SourcePostFilters;
  /** When the set became Moje filtry; `null` = prefilled only, not saved yet (§1.71). */
  savedAt: string | null;
  alerts: { enabled: boolean; cadence: SavedSearchCadence };
  /** The set's city as stored; the results page searches `PRIMARY_ACTIVE_CITY` regardless. */
  city: string | null;
  /** Anything set at all — without it the page shows no chip and no prefill. */
  hasAny: boolean;
  /** A tenant with a row. `false` = guest, owner, agent: nothing to prefill or save. */
  isTenant: boolean;
  /** „Ostatnio wybrane” places kept on the account (§1.70); a non-tenant keeps them in the browser. */
  recentPlaces: RecentPlace[];
  /** When the tenant last had Moje filtry on screen — the window of the flag's „+N” badge (§1.6). */
  lastViewedAt: string | null;
  /** Last activity on the account — the 15-day alert inactivity clock (AlertEmails §4); Settings reads it for the dormancy note (C6). */
  lastActiveAt: string | null;
  /** Why the sender or an unsubscribe link switched alerts off (5.13) — the Settings dormancy note reads it. */
  alertsOffReason: 'inactivity' | 'unsubscribe' | null;
}

export const PUSTE: MojeWyszukiwanie = {
  filters: {},
  savedAt: null,
  alerts: { enabled: false, cadence: 'daily' },
  city: null,
  hasAny: false,
  isTenant: false,
  recentPlaces: [],
  lastViewedAt: null,
  lastActiveAt: null,
  alertsOffReason: null,
};
export const KLUCZ_MOJEGO_WYSZUKIWANIA = ['moje-wyszukiwanie'] as const;

/**
 * The generated `Database` type knows only `public`; the RPCs live in `flat` (served by
 * PostgREST like `flat.offers_ranked` in `src/lib/serpRead`). Typed narrowly here so the two
 * calls stay honest without a generated `flat` schema.
 */
interface WynikRpc<T> {
  data: T | null;
  error: { message: string; code?: string } | null;
}
interface KlientFlat {
  schema(name: 'flat'): {
    rpc(fn: 'get_my_filters'): PromiseLike<WynikRpc<SavedSearchRow[]>>;
    rpc(fn: 'save_my_filters', args: { p_doc: SaveMyFiltersInput }): PromiseLike<WynikRpc<SavedSearchRow[]>>;
  };
}
const flat = () => (supabase as unknown as KlientFlat).schema('flat');

export function zWiersza(row: SavedSearchRow): MojeWyszukiwanie {
  const surowe = fromSavedFiltersDoc(row.filters);
  // Reguły zawierania przy wczytaniu (audyt 19.10): zestaw zapisany, gdy pole na pulpicie je omijało,
  // może mieć parę „Rembertów + Nowy Rembertów”. Tu, a nie w savedSearchDoc — tamten plik czyta też
  // funkcja Deno `send-filter-alerts`, która nie zna aliasów `@/` z areaSelection.
  const filters = surowe.areas ? { ...surowe, areas: uporzadkujObszary(surowe.areas) } : surowe;
  return {
    filters,
    savedAt: row.saved_at,
    alerts: { enabled: row.alerts_enabled, cadence: row.cadence },
    city: row.city,
    hasAny: Object.keys(filters).length > 0,
    isTenant: true,
    recentPlaces: poprawnaLista(row.recent_places),
    lastViewedAt: row.last_viewed_at ?? null,
    lastActiveAt: row.last_active_at ?? null,
    alertsOffReason: row.alerts_off_reason ?? null,
  };
}

export function useMojeWyszukiwanie(opts?: { enabled?: boolean }) {
  return useQuery<MojeWyszukiwanie>({
    queryKey: KLUCZ_MOJEGO_WYSZUKIWANIA,
    enabled: opts?.enabled ?? true,
    staleTime: 5 * 60_000,
    // Every visit to the results page reads afresh: the row is also written by the claim
    // prefill and the co-tenant seed (no shared key to invalidate), and „Zapisz” compares with it.
    refetchOnMount: 'always',
    retry: false,
    queryFn: readMyFilters,
  });
}

/**
 * Reads the tenant's set (creating the empty unsaved row when none exists yet — the RPC does that,
 * as the definer, for the caller's own id). `PUSTE` for a guest or non-tenant.
 */
export async function readMyFilters(): Promise<MojeWyszukiwanie> {
  const { data: sesja } = await supabase.auth.getSession();
  if (!sesja.session) return PUSTE;
  const { data, error } = await flat().rpc('get_my_filters');
  // 42501 = no `users` row behind this session yet (mid-signup): no set, not a failure.
  if (error?.code === '42501') return PUSTE;
  if (error) throw error;
  const row = data?.[0];
  return row ? zWiersza(row) : PUSTE;
}

/** Whether a set may be SAVED (§1.3: city + a price limit). Mirrors `flat.saved_filters_has_price`. */
export const maCeneDoZapisu = maCene;

/**
 * Writes the whole filter state as the tenant's set and returns the row as stored.
 *
 * `saved: true` marks the set as Moje filtry (first save; the RPC refuses it without a price —
 * that failure surfaces to the caller, §1.3). `saved: false` is the „Cofnij” of the FIRST save
 * only (C1): the row goes back to an unsaved prefill and its alerts go off (they need a saved set,
 * migration 20261016…_save_my_filters_cofnij_pierwszy_zapis). A saved set without a price is
 * rejected by the RPC (§1.3).
 *
 * The city is the one the results page searches (`PRIMARY_ACTIVE_CITY`): the districts in the
 * document are its districts, whatever city the row was seeded with from the profile. Onboarding's
 * city step passes the city the tenant chose (`opts.city`, a `supported_cities` name — the RPC
 * refuses any other).
 *
 * Without `saved` the call leaves `saved_at` as it is: an unsaved prefill stays unsaved (the
 * onboarding answers, §1.71, §1.89) and a saved set stays Moje filtry.
 */
export async function saveMyFilters(
  filters: SourcePostFilters,
  opts: { saved?: boolean; alerts?: { enabled: boolean; cadence: SavedSearchCadence }; city?: string } = {},
): Promise<MojeWyszukiwanie> {
  const input: SaveMyFiltersInput = {
    city: opts.city ?? PRIMARY_ACTIVE_CITY,
    filters: toSavedFiltersDoc(filters),
    filters_version: SAVED_FILTERS_VERSION,
  };
  if (opts.saved !== undefined) input.saved = opts.saved;
  // In the same call as `saved`, so the set and its alerts land together (the onboarding opt-in, C5).
  if (opts.alerts) {
    input.alerts_enabled = opts.alerts.enabled;
    input.cadence = opts.alerts.cadence;
  }
  const { data, error } = await flat().rpc('save_my_filters', { p_doc: input });
  if (error) throw error;
  const row = data?.[0];
  if (!row) throw new Error('save_my_filters: no row returned');
  return zWiersza(row);
}

/**
 * Writes only „Ostatnio wybrane” (§1.70) — a partial `save_my_filters` call: the filters, the saved
 * flag and the alerts stay as they are. Returns the row as stored.
 */
export async function saveRecentPlaces(lista: RecentPlace[]): Promise<MojeWyszukiwanie> {
  const { data, error } = await flat().rpc('save_my_filters', { p_doc: { recent_places: lista } });
  if (error) throw error;
  const row = data?.[0];
  if (!row) throw new Error('save_my_filters: no row returned');
  return zWiersza(row);
}

/** Stamps `last_viewed_at` — Moje filtry are on screen, so the „+N” badge starts from zero (§1.6). */
export async function touchViewed(): Promise<MojeWyszukiwanie> {
  const { data, error } = await flat().rpc('save_my_filters', { p_doc: { touch_viewed: true } });
  if (error) throw error;
  const row = data?.[0];
  if (!row) throw new Error('save_my_filters: no row returned');
  return zWiersza(row);
}

/**
 * Stamps `last_active_at` — a logged-in visit, the activity that keeps alerts on (AlertEmails §4).
 * Called once per Warsaw day per account by `useStempelAktywnosci`. A touch-only call never creates
 * the set (migration 20261016070741): `null` = the tenant has no set yet, nothing was stamped.
 */
export async function touchActive(): Promise<MojeWyszukiwanie | null> {
  const { data, error } = await flat().rpc('save_my_filters', { p_doc: { touch_active: true } });
  if (error) throw error;
  const row = data?.[0];
  return row ? zWiersza(row) : null;
}

/**
 * Writes only the e-mail alert settings (C3, the one-time question): a partial `save_my_filters`
 * call with `alerts_enabled` + `cadence`; the filters and `saved_at` stay as they are. Alerts need
 * a saved set — the RPC refuses them on an unsaved prefill and that failure reaches the caller.
 */
export async function saveAlerts(alerts: { enabled: boolean; cadence: SavedSearchCadence }): Promise<MojeWyszukiwanie> {
  const { data, error } = await flat().rpc('save_my_filters', { p_doc: { alerts_enabled: alerts.enabled, cadence: alerts.cadence } });
  if (error) throw error;
  const row = data?.[0];
  if (!row) throw new Error('save_my_filters: no row returned');
  return zWiersza(row);
}
